import type { FavoriteViewFolder } from '@/api/favorites'
import { VpIcon } from '@vtmn-play/react'
import { VpLoader } from '@vtmn-play/react/headless'
import { Trans, useTranslation } from 'react-i18next'
import { CHECK_EMOJI_UNIFIED } from '@/components/FavoriteViews/FavoriteViewEmoji'
import { ALL_SAVED_VIEWS_ID, FavoriteViewFolderCard } from '@/components/FavoriteViews/FavoriteViewFolderCard'
import { NoResultsIllustration } from '@/components/NoResultsIllustration'
import { useFavoriteViewsPageActionsContext } from './useFavoriteViewsPageActionsContext'
import './favorite-views-folder-overview.scss'

interface FavoriteViewsFolderOverviewProps {
  isLoading: boolean
  folders: FavoriteViewFolder[]
  allViewCount: number
}

export function FavoriteViewsFolderOverview({
  isLoading,
  folders,
  allViewCount,
}: Readonly<FavoriteViewsFolderOverviewProps>) {
  const { t } = useTranslation()
  const { onOpenFolder, onRenameFolder, onDeleteFolder } = useFavoriteViewsPageActionsContext()

  if (isLoading)
    return <div className="favorite-views-page__loading"><VpLoader size="large" /></div>

  return (
    <div className="favorite-views-page__folder-overview">
      <ul className="favorite-views-page__all-views-list">
        <FavoriteViewFolderCard
          folder={{
            id: ALL_SAVED_VIEWS_ID,
            name: t('favorite-views.panel.all-saved-views'),
            emoji: CHECK_EMOJI_UNIFIED,
            viewCount: allViewCount,
          }}
          disabled={allViewCount === 0}
          onSelect={() => onOpenFolder('all')}
        />
      </ul>
      <ul className="favorite-views-page__folder-grid">
        {folders.map(folder => (
          <FavoriteViewFolderCard
            key={folder.id}
            folder={folder}
            onSelect={() => onOpenFolder(folder.id)}
            onRename={() => onRenameFolder(folder)}
            onDelete={() => onDeleteFolder(folder)}
          />
        ))}
      </ul>
      {allViewCount === 0 && (
        <div className="favorite-views-page__empty" data-testid="favorite-views-no-favorites">
          <NoResultsIllustration />
          <h3>{t('favorite-views.empty.no-favorites-title')}</h3>
          <p>
            <Trans
              i18nKey="favorite-views.empty.no-favorites-description"
              components={{ favoriteIcon: <VpIcon name="heart" /> }}
            />
          </p>
        </div>
      )}
    </div>
  )
}
